import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Smartphone, ShieldCheck, Check, Search, Pencil, Star, Cake, X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { parseBulkContacts } from "@/lib/relationships.functions";
import { contactsPickerSupported, pickContacts } from "@/lib/native-contacts";

export type PickedContact = {
  name: string;
  phone?: string;
  email?: string;
  company?: string;
  category?: string;
  isVip?: boolean;
  birthday?: string; // YYYY-MM-DD (year may be 0001 if unknown)
};

const CATEGORIES = ["Family", "Close Friends", "Friends", "Professional", "Investors", "Mentors"];

function emptyRow(): PickedContact {
  return { name: "", phone: "", email: "", company: "", category: undefined, isVip: false };
}

/**
 * Split a single free-text line into { name, company }.
 * Looks for the first separator: , | @ ` at ` ` - `
 * Falls back to treating the whole string as the name.
 */
export function parseContactLine(input: string): { name: string; company: string } {
  const cleaned = input.replace(/\s+/g, " ").trim();
  if (!cleaned) return { name: "", company: "" };
  const re = /\s*(?:,|\||@| at | - )\s*/i;
  const m = cleaned.match(re);
  if (!m || m.index === undefined) return { name: cleaned, company: "" };
  const name = cleaned.slice(0, m.index).trim();
  const company = cleaned.slice(m.index + m[0].length).trim();
  return { name: name || cleaned, company };
}

export function nativeContactsAvailable() {
  return contactsPickerSupported();
}

/* ---------------- 1a. Permission ---------------- */

export function ContactsPermissionStep({
  onAllow,
  onManual,
}: {
  onAllow: () => void;
  onManual: () => void;
}) {
  const hasNative = nativeContactsAvailable();
  return (
    <div className="space-y-4">
      <div className="rounded-2xl glass-soft p-5 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Pyng only saves the people you choose. Nothing is uploaded in bulk, and your address book stays on your device.
        </p>
      </div>

      <button
        type="button"
        onClick={onAllow}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-4 text-sm font-medium text-primary-foreground"
      >
        <Smartphone className="h-4 w-4" />
        {hasNative ? "Allow contact access" : "Pick from your contacts"}
      </button>

      <button
        type="button"
        onClick={onManual}
        className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
      >
        Add manually instead
      </button>
    </div>
  );
}

/* ---------------- 1b. Pick / Manual ---------------- */

export function ContactsPickStep({
  value,
  onChange,
  mode,
  onSwitchMode,
  autoOpenNative,
}: {
  value: PickedContact[];
  onChange: (next: PickedContact[]) => void;
  mode: "native" | "manual";
  onSwitchMode: (next: "native" | "manual") => void;
  autoOpenNative?: boolean;
}) {
  const [manualRows, setManualRows] = useState<PickedContact[]>(
    value.length ? value : [emptyRow()],
  );
  const [pool, setPool] = useState<PickedContact[]>([]); // results from native picker
  const [query, setQuery] = useState("");
  const opened = useRef(false);
  const [manualSubMode, setManualSubMode] = useState<"rows" | "paste">("rows");
  const [pasteText, setPasteText] = useState("");
  const parse = useServerFn(parseBulkContacts);
  const parseMut = useMutation({
    mutationFn: () => parse({ data: { text: pasteText.trim() } }),
    onSuccess: ({ contacts }: any) => {
      if (!contacts?.length) {
        toast.error("Pyng couldn't find any people in that text");
        return;
      }
      const key = (c: PickedContact) => `${c.name}|${c.phone ?? ""}`;
      const next = [...value];
      const mapped: PickedContact[] = contacts.map((c: any) => ({
        name: c.name,
        email: c.email,
        phone: c.phone,
        company: c.company,
      }));
      for (const c of mapped) if (c.name && !next.some((m) => key(m) === key(c))) next.push(c);
      onChange(next);
      setPasteText("");
      setManualSubMode("rows");
      toast.success(`Added ${mapped.length} ${mapped.length === 1 ? "person" : "people"}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Couldn't parse"),
  });

  const syncManual = (next: PickedContact[]) => {
    setManualRows(next);
    // A row only counts once we have both a name and an affiliation — the
    // affiliation is the key the scan uses to pick the right person.
    onChange(next.filter((r) => r.name.trim().length > 0 && (r.company ?? "").trim().length > 0));
  };

  async function pickNative() {
    if (!contactsPickerSupported()) {
      toast.error("Your browser can't open phone contacts — add manually");
      onSwitchMode("manual");
      return;
    }
    try {
      const picked = await pickContacts();
      if (!picked.length) return;
      const mapped: PickedContact[] = picked
        .map((p) => ({ name: p.name, phone: p.phone, email: p.email }))
        .filter((c) => c.name.trim().length > 0);
      // merge into pool, dedup by name+phone
      const key = (c: PickedContact) => `${c.name}|${c.phone ?? ""}`;
      const merged = [...pool];
      for (const c of mapped) if (!merged.some((m) => key(m) === key(c))) merged.push(c);
      setPool(merged);
      // auto-include newly picked
      const next = [...value];
      for (const c of mapped) if (!next.some((m) => key(m) === key(c))) next.push(c);
      onChange(next);
      toast.success(`Added ${mapped.length} contact${mapped.length === 1 ? "" : "s"}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't open contacts");
    }
  }

  useEffect(() => {
    if (mode === "native" && autoOpenNative && !opened.current) {
      opened.current = true;
      void pickNative();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, autoOpenNative]);

  /* ---- native checklist mode ---- */
  if (mode === "native") {
    const isSelected = (c: PickedContact) =>
      value.some((v) => v.name === c.name && (v.phone ?? "") === (c.phone ?? ""));
    const toggle = (c: PickedContact) => {
      if (isSelected(c)) onChange(value.filter((v) => !(v.name === c.name && (v.phone ?? "") === (c.phone ?? ""))));
      else onChange([...value, c]);
    };
    const filtered = pool.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={pickNative}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground"
        >
          <Smartphone className="h-4 w-4" /> {pool.length ? "Pick more contacts" : "Open phone contacts"}
        </button>

        {pool.length > 0 && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search picked contacts"
                className="w-full rounded-2xl glass-soft pl-9 pr-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground/60"
              />
            </div>
            <div className="space-y-1.5 max-h-80 overflow-auto">
              {filtered.map((c, i) => {
                const sel = isSelected(c);
                const initials = c.name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <button
                    key={`${c.name}-${i}`}
                    type="button"
                    onClick={() => toggle(c)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition",
                      sel ? "bg-primary/15 border border-primary/40" : "glass-soft",
                    )}
                  >
                    <div className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center text-[11px] text-muted-foreground">
                      {initials || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{c.name}</p>
                      {c.phone && <p className="text-[11px] text-muted-foreground truncate">{c.phone}</p>}
                    </div>
                    <div
                      className={cn(
                        "h-5 w-5 rounded-full flex items-center justify-center border",
                        sel ? "bg-primary border-primary text-primary-foreground" : "border-white/20",
                      )}
                    >
                      {sel && <Check className="h-3 w-3" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <button
          type="button"
          onClick={() => onSwitchMode("manual")}
          className="block w-full text-center text-xs text-muted-foreground hover:text-foreground pt-1"
        >
          Add manually instead
        </button>
      </div>
    );
  }

  /* ---- manual rows mode ---- */
  const updateField = (i: number, field: "name" | "company", raw: string) => {
    syncManual(manualRows.map((r, idx) => (idx === i ? { ...r, [field]: raw } : r)));
  };
  const remove = (i: number) => {
    syncManual(manualRows.filter((_, idx) => idx !== i));
  };
  const add = () => {
    syncManual([...manualRows, emptyRow()]);
  };

  return (
    <div className="space-y-3">
      {nativeContactsAvailable() ? (
        <button
          type="button"
          onClick={() => onSwitchMode("native")}
          className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Smartphone className="h-3.5 w-3.5" /> Pick from phone contacts
        </button>
      ) : (
        <div className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-4 py-2.5 text-xs text-muted-foreground/50">
          <Smartphone className="h-3.5 w-3.5" /> Import from phone contacts · Coming soon
        </div>
      )}

      <div className="flex items-start gap-2 px-1">
        <ShieldCheck className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Pyng only saves the people you choose. Nothing is uploaded in bulk.
        </p>
      </div>

      <div className="flex items-center gap-1 rounded-full bg-white/5 p-0.5 text-[11px]">
        <button
          type="button"
          onClick={() => setManualSubMode("rows")}
          className={cn(
            "flex-1 rounded-full px-3 py-1.5 transition",
            manualSubMode === "rows" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          Type rows
        </button>
        <button
          type="button"
          onClick={() => setManualSubMode("paste")}
          className={cn(
            "flex-1 rounded-full px-3 py-1.5 transition",
            manualSubMode === "paste" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          Paste a list
        </button>
      </div>

      {manualSubMode === "paste" ? (
        <div className="space-y-2">
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={"Paste names — one per line, or a messy list.\nPyng splits it."}
            rows={5}
            className="w-full min-h-[120px] bg-transparent text-sm text-foreground outline-none resize-y placeholder:text-muted-foreground/40 border-b border-white/10 focus:border-primary pb-2 transition-colors"
          />
          <button
            type="button"
            onClick={() => pasteText.trim().length >= 2 && parseMut.mutate()}
            disabled={pasteText.trim().length < 2 || parseMut.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {parseMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {parseMut.isPending ? "Pyng is reading…" : "Parse with Pyng"}
          </button>
          {value.length > 0 && (
            <p className="px-1 text-[11px] text-muted-foreground">
              {value.length} added so far. Switch to "Type rows" to edit.
            </p>
          )}
        </div>
      ) : (
      <>
      <p className="px-1 pt-1 text-[11px] text-muted-foreground">
        Name and where they work — e.g. <span className="italic">Jane Smith · Stripe</span>
      </p>

      <div className="space-y-2">
        {manualRows.map((row, i) => {
          const nameVal = row.name ?? "";
          const companyVal = row.company ?? "";
          const needsCompany = nameVal.trim().length > 0 && companyVal.trim().length === 0;
          return (
            <div key={i} className="rounded-2xl glass-soft p-3 space-y-1.5">
              <div className="flex items-end gap-2">
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                  <input
                    value={nameVal}
                    onChange={(e) => updateField(i, "name", e.target.value)}
                    placeholder="Full name"
                    autoComplete="off"
                    className="flex-1 min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/40 border-b border-white/10 focus:border-primary pb-1 transition-colors"
                  />
                  <input
                    value={companyVal}
                    onChange={(e) => updateField(i, "company", e.target.value)}
                    placeholder="Company or affiliation"
                    autoComplete="off"
                    className="flex-1 min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/40 border-b border-white/10 focus:border-primary pb-1 transition-colors"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-foreground p-1"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {needsCompany && (
                <p className="text-[10px] text-muted-foreground/80 leading-snug">
                  Add a company or affiliation so we find the right person.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" /> Add another
      </button>
      </>
      )}
    </div>
  );
}

/* ---------------- 1c. Confirm ---------------- */

export function ContactsConfirmStep({
  value,
  onChange,
  onAddMore,
}: {
  value: PickedContact[];
  onChange: (next: PickedContact[]) => void;
  onAddMore: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);

  const setCategory = (i: number, c: string | undefined) =>
    onChange(value.map((r, idx) => (idx === i ? { ...r, category: c } : r)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const setBirthday = (i: number, b: string | undefined) =>
    onChange(value.map((r, idx) => (idx === i ? { ...r, birthday: b } : r)));

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {value.map((c, i) => {
          const initials = c.name.trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
          const open = editing === i;
          const subtitle = [c.category ?? "No category", c.company, c.phone].filter(Boolean).join(" · ");
          return (
            <div key={i} className="rounded-2xl glass-soft p-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center text-xs text-muted-foreground">
                  {initials || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate flex items-center gap-1.5">
                    {c.name}
                    {c.isVip && <Star className="h-3 w-3 fill-current text-primary" />}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(open ? null : i)}
                  className="text-muted-foreground hover:text-foreground p-1.5"
                  aria-label="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-foreground p-1.5"
                  aria-label="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {open && (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCategory(i, c.category === cat ? undefined : cat)}
                        className={cn(
                          "rounded-full px-2.5 py-0.5 text-[10px] transition",
                          c.category === cat
                            ? "bg-primary text-primary-foreground"
                            : "bg-white/5 text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <BirthdayInline
                    value={c.birthday}
                    onChange={(b) => setBirthday(i, b)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onAddMore}
        className="flex w-full items-center justify-center gap-2 rounded-2xl glass-soft px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" /> Add more people
      </button>
    </div>
  );
}

function BirthdayInline({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
}) {
  const parsed = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const [open, setOpen] = useState(!!parsed);
  const [m, setM] = useState(parsed?.[2] ?? "");
  const [d, setD] = useState(parsed?.[3] ?? "");
  const [y, setY] = useState(parsed && parsed[1] !== "0001" ? parsed[1] : "");

  function commit(month: string, day: string, year: string) {
    const mn = parseInt(month, 10);
    const dn = parseInt(day, 10);
    if (!mn || !dn || mn < 1 || mn > 12 || dn < 1 || dn > 31) {
      onChange(undefined);
      return;
    }
    const yy = year && /^\d{4}$/.test(year) ? year : "0001";
    onChange(`${yy}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`);
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <Cake className="h-3 w-3" /> Add birthday
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <Cake className="h-3 w-3 text-primary" />
      <input inputMode="numeric" maxLength={2} placeholder="MM" value={m}
        onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setM(v); commit(v, d, y); }}
        className="h-7 w-10 rounded-md border border-input bg-background px-1.5 text-center text-xs" />
      <span>/</span>
      <input inputMode="numeric" maxLength={2} placeholder="DD" value={d}
        onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setD(v); commit(m, v, y); }}
        className="h-7 w-10 rounded-md border border-input bg-background px-1.5 text-center text-xs" />
      <span>/</span>
      <input inputMode="numeric" maxLength={4} placeholder="YYYY" value={y}
        onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setY(v); commit(m, d, v); }}
        className="h-7 w-14 rounded-md border border-input bg-background px-1.5 text-center text-xs" />
      <button type="button" onClick={() => { setOpen(false); setM(""); setD(""); setY(""); onChange(undefined); }}
        className="ml-1 text-muted-foreground/70 hover:text-foreground" aria-label="Remove birthday">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
